/* global grecaptcha */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import '../Forms.sass';
import Input from '../Input';
import ErrorLabel from '../ErrorLabel';
import Textarea from '../Textarea';
import Checkbox from '../Checkbox';
import ButtonPrimary from '../../../uikit/Buttons/Primary';
import SuccessPopup from '../../SuccessPopup';

import {
	CAPTCHA_KEY,
	FORMS_ENDPOINTS,
	VALIDATION_PATTERNS,
} from '../../../utils/constants';
import { constructDevSrc } from '../../../utils/helpers';

import IInputs from '../../../interfaces/IInputs';

interface Props {
	nameLabel: string;
	surnameLabel: string;
	emailLabel: string;
	companyNameLabel: string;
	privacyPolicyLabelText: string;
	partnerAdditionalInfoLabel: string;
	partnerAdditionalInfoPlaceholder: string;
	submitButtonLabel: string;
}

function PartnerForm({
	nameLabel,
	surnameLabel,
	emailLabel,
	companyNameLabel,
	privacyPolicyLabelText,
	partnerAdditionalInfoLabel,
	partnerAdditionalInfoPlaceholder,
	submitButtonLabel,
}: Props) {
	const {
		register,
		handleSubmit,
		formState: { errors, dirtyFields, isValid },
		watch,
		reset,
	} = useForm<IInputs>({ mode: 'onChange' });

	const watchAllFields = watch();
	const [isFull, setIsFull] = useState<boolean>(false);
	const [isSucceed, setIsSucceed] = useState<boolean>(false);
	const [isSending, setIsSending] = useState<boolean>(false);

	useEffect(() => {
		isSucceed
			? document.body.classList.add('body-overflow-hidden')
			: document.body.classList.remove('body-overflow-hidden');
	}, [isSucceed]);

	const onClickHandler = () => {
		reset();
		setIsSucceed(false);
		setIsFull(false);
		setIsSending(false);
	};

	useEffect(() => {
		const requiredFields = [
			'name',
			'surname',
			'email',
			'company',
			'skills',
			'expertise',
			'agreement',
		];
		/* @ts-ignore */
		const allTrue = requiredFields.every((field) => {
			/* @ts-ignore */
			if (watchAllFields[field] != false) {
				return true;
			}
			return false;
		});

		if (allTrue && !isSending) {
			setIsFull(true);
		} else {
			setIsFull(false);
		}
	}, [watchAllFields, isSending]);

	const onSubmit = async (data: IInputs) => {
		setIsSending(true);

		grecaptcha.ready(() => {
			grecaptcha
				.execute(CAPTCHA_KEY, {
					action: 'partner',
				})
				.then((result) => {
					fetch(constructDevSrc(FORMS_ENDPOINTS.partnerForm), {
						method: 'POST',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({ 'g-recaptcha-response': result, ...data }),
					})
						.then((response) => {
							if (response.status === 200) {
								setIsSucceed(true);
								setIsSending(false);
							}
						})
						.catch((err) => {
							console.log(err.message);
						});
				});
		});
	};
	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="form-row">
					<div className="form-element form-element--double">
						<Input
							invalid={!!errors.name}
							valid={
								watchAllFields.name !== '' && dirtyFields.name && !errors.name
							}
							inputName="name"
							label={nameLabel || 'Name'}
							required
							register={register}
							rules={{
								required: 'This field is required',
								minLength: {
									value: 2,
									message:
										'Minimum required length for this field is 2 characters',
								},
								pattern: {
									value: VALIDATION_PATTERNS.name,
									message: 'Entered value should contain characters only',
								},
							}}
						/>
						{errors.name && <ErrorLabel message={errors.name.message} />}
					</div>
					<div className="form-element form-element--double">
						<Input
							invalid={!!errors.surname}
							valid={
								watchAllFields.surname !== '' &&
								dirtyFields.surname &&
								!errors.surname
							}
							inputName="surname"
							label={surnameLabel || 'Surname'}
							required
							register={register}
							rules={{
								required: 'This field is required',
								minLength: {
									value: 2,
									message:
										'Minimum required length for this field is 2 characters',
								},
								pattern: {
									value: VALIDATION_PATTERNS.name,
									message: 'Entered value should contain characters only',
								},
							}}
						/>
						{errors.surname && <ErrorLabel message={errors.surname.message} />}
					</div>
				</div>
				<div className="form-row">
					<div className="form-element">
						<Input
							invalid={!!errors.company}
							valid={
								watchAllFields.company !== '' &&
								dirtyFields.company &&
								!errors.company
							}
							inputName="company"
							label={companyNameLabel || 'Company'}
							required
							register={register}
							rules={{
								required: 'This field is required',
								minLength: {
									value: 2,
									message:
										'Minimum required length for this field is 2 characters',
								},
							}}
						/>
						{errors.company && <ErrorLabel message={errors.company.message} />}
					</div>
				</div>
				<div className="form-row">
					<div className="form-element">
						<Input
							invalid={!!errors.email}
							valid={
								watchAllFields.email !== '' &&
								dirtyFields.email &&
								!errors.email
							}
							inputName="email"
							label={emailLabel || 'E-mail'}
							required
							register={register}
							rules={{
								required: 'This field is required',
								minLength: {
									value: 2,
									message:
										'Minimum required length for this field is 2 characters',
								},
								pattern: {
									value: VALIDATION_PATTERNS.email,
									message: 'Entered value does not match email format',
								},
							}}
						/>
						{errors.email && <ErrorLabel message={errors.email.message} />}
					</div>
				</div>
				<div className="form-row">
					<div className="form-element">
						<Textarea
							invalid={!!errors.info}
							valid={
								watchAllFields.info !== '' && dirtyFields.info && !errors.info
							}
							inputName="info"
							label={partnerAdditionalInfoLabel || 'Additional information'}
							placeholder={partnerAdditionalInfoPlaceholder || ''}
							register={register}
						/>
					</div>
				</div>
				<div className="form-row form-row--gap">
					<div className="form-element">
						<Checkbox
							register={register}
							inputName="agreement"
							required
							rules={{ required: 'This field is required' }}
							label={privacyPolicyLabelText}
						/>
						{errors.agreement && (
							<ErrorLabel message={errors.agreement.message} />
						)}
					</div>
				</div>
				<div className="form-row form-row--gap-lg form-row--push-right">
					<ButtonPrimary
						id="submit_partner"
						disabled={!isFull || Object.keys(errors).length !== 0}
						type="submit"
						submitButtonLabel={
							isSending ? 'Sending' : submitButtonLabel || 'Submit'
						}
					/>
				</div>
			</form>
			{isSucceed && (
				<SuccessPopup
					title="Thank you for contacting us!"
					content="Thank you for your interest in becoming a Partner and supporting our conference. We will contact you  in a few business days. <br><br> Follow us on <a href='https://www.facebook.com/itnonstop.conference' target='_blank'>Facebook</a> and <a href='https://twitter.com/it_nonstop' target='_blank'>Twitter</a>. We are there for you."
					closePopup={async () => {
						onClickHandler();
					}}
					isActive={isSucceed}
				/>
			)}
		</>
	);
}

export default PartnerForm;
